/**
 * Memory Stream Story Type Definitions
 * 
 * This file defines the four distinct story types supported by Memory Stream:
 * 1. INDIVIDUAL - Single person life story (Memory Stream For One)
 * 2. INDIVIDUAL_GIFT - Single person life story given as a gift (Memory Stream For One - Gift)
 * 3. COLLABORATIVE - Two people sharing their story together (Memory Stream For Two)
 * 4. COLLABORATIVE_GIFT - Two people story given as a gift (Memory Stream For Two - Gift)
 */

export const STORY_TYPES = {
  INDIVIDUAL: 'individual',
  INDIVIDUAL_GIFT: 'individual_gift', 
  COLLABORATIVE: 'collaborative',
  COLLABORATIVE_GIFT: 'collaborative_gift'
};

export const STORY_TYPE_LABELS = {
  [STORY_TYPES.INDIVIDUAL]: 'Memory Stream For One',
  [STORY_TYPES.INDIVIDUAL_GIFT]: 'Memory Stream For One (For someone else)',
  [STORY_TYPES.COLLABORATIVE]: 'Memory Stream For Two', 
  [STORY_TYPES.COLLABORATIVE_GIFT]: 'Memory Stream For Two (For someone else)'
};

export const STORY_TYPE_DESCRIPTIONS = {
  [STORY_TYPES.INDIVIDUAL]: 'Create your personal life story with AI-generated questions',
  [STORY_TYPES.INDIVIDUAL_GIFT]: 'Gift a personal life story experience to someone special',
  [STORY_TYPES.COLLABORATIVE]: 'Create a shared story with another person, both contributing perspectives',
  [STORY_TYPES.COLLABORATIVE_GIFT]: 'Gift a collaborative story experience for two people to share together'
};

/**
 * Utility functions for story type management
 */
export const StoryTypeUtils = {
  /**
   * Determine story type from form data
   */
  getStoryType: (formData) => {
    const isCollaborative = formData.isCollaborativeStory;
    const isGift = formData.isGiftStory;
    
    if (isCollaborative && isGift) {
      return STORY_TYPES.COLLABORATIVE_GIFT;
    } else if (isCollaborative && !isGift) {
      return STORY_TYPES.COLLABORATIVE;
    } else if (!isCollaborative && isGift) {
      return STORY_TYPES.INDIVIDUAL_GIFT;
    } else {
      return STORY_TYPES.INDIVIDUAL;
    }
  },

  /**
   * Check if story type is collaborative
   */
  isCollaborativeType: (storyType) => {
    return storyType === STORY_TYPES.COLLABORATIVE || storyType === STORY_TYPES.COLLABORATIVE_GIFT;
  },

  /**
   * Check if story type is a gift
   */
  isGiftType: (storyType) => {
    return storyType === STORY_TYPES.INDIVIDUAL_GIFT || storyType === STORY_TYPES.COLLABORATIVE_GIFT;
  },

  /**
   * Check if story type is individual
   */
  isIndividualType: (storyType) => {
    return storyType === STORY_TYPES.INDIVIDUAL || storyType === STORY_TYPES.INDIVIDUAL_GIFT;
  },

  /**
   * Get required product access for story type
   */
  getRequiredProductAccess: (storyType) => {
    if (StoryTypeUtils.isCollaborativeType(storyType)) {
      return 'isCollaborativeProduct';
    }
    return 'storyTokens'; // Individual stories need basic tokens
  },

  /**
   * Get the appropriate question generation function for story type
   */
  getQuestionGenerationFunction: (storyType) => {
    if (StoryTypeUtils.isCollaborativeType(storyType)) {
      return 'generateCollaborativeStoryPrompts';
    }
    return 'generateEnhancedStoryPrompts'; // For individual stories
  },

  /**
   * Validate story type data requirements
   */
  validateStoryTypeData: (storyType, formData) => {
    const errors = [];

    switch (storyType) {
      case STORY_TYPES.INDIVIDUAL:
        if (!formData.name) errors.push('Name is required for individual stories');
        break;
        
      case STORY_TYPES.INDIVIDUAL_GIFT:
        if (!formData.recipientName) errors.push('Recipient name is required for gift stories');
        if (!formData.purchaserName) errors.push('Gift giver name is required for gift stories');
        break;
        
      case STORY_TYPES.COLLABORATIVE:
        if (!formData.name) errors.push('Your name is required for collaborative stories');
        if (!formData.collaboratorName) errors.push('Collaborator name is required');
        if (!formData.collaboratorEmail) errors.push('Collaborator email is required');
        break;
        
      case STORY_TYPES.COLLABORATIVE_GIFT:
        if (!formData.recipientName) errors.push('Recipient name is required for gift stories');
        if (!formData.purchaserName) errors.push('Gift giver name is required');
        if (!formData.collaboratorName) errors.push('Second person name is required');
        if (!formData.collaboratorEmail) errors.push('Second person email is required');
        break;
        
      default:
        errors.push('Invalid story type');
    }

    return errors;
  }
};

/**
 * Legacy compatibility - convert old boolean flags to story type
 */
export const convertLegacyToStoryType = (isCollaborativeStory, isGiftStory) => {
  return StoryTypeUtils.getStoryType({ isCollaborativeStory, isGiftStory });
};

/**
 * Convert story type back to legacy boolean flags (for backward compatibility)
 */
export const convertStoryTypeToLegacy = (storyType) => {
  return {
    isCollaborativeStory: StoryTypeUtils.isCollaborativeType(storyType),
    isGiftStory: StoryTypeUtils.isGiftType(storyType)
  };
};
